import { useState, useEffect, useRef } from 'react';
import debounce from 'lodash/debounce';
import { getApplications } from '../api/application';

export const useApplications = (initialFilters = {}, initialPageSize = 10) => {
    const initialFiltersRef = useRef(initialFilters);
    const [data, setData] = useState([]);                   // Applications Data as a response
    const [isLoading, setIsLoading] = useState(false);      // Loaders state
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({          // For paginated responses
        totalPages: 0,
        currentPage: 0,
        totalElements: 0,
        pageSize: initialPageSize
    });

    const [filters, setFilters] = useState(() => ({
        ...initialFiltersRef.current // Initialize filters only once
    }));

    const fetchData = async (params) => {
        try {
            setIsLoading(true);
            setError(null);
            console.log('Setting loading to true');

            const response = await getApplications(params);
            console.log("Response from Get Applications API: ", response);

            if (response) {
                setData(response.data);
                setPagination(prev => ({
                    ...prev,
                    totalPages: response.totalPages,
                    currentPage: response.pageNumber,
                    totalElements: response.totalElements
                }));
            }

        } catch (err) {
            console.error("Error fetching applications: ", err);
            setError(err.message || 'An error occurred while fetching your data. Try again in a few seconds, or contact support if it persists.');
        } finally {
            setIsLoading(false);
        }
    };

    // Create debounced version of fetchData
    const debouncedFetchData = useRef(
        debounce((params) => fetchData(params), 500)
    ).current;

    const fetchApplications = async (page = 0) => {
        let params = {
            page,
            size: pagination.pageSize,
            ...(filters.applicationId && { applicationId: filters.applicationId }),
            ...(filters.applicationStatus && { applicationStatus: filters.applicationStatus }),
            ...(filters.sortBy && {
                sortBy: filters.sortBy,
                sortDirection: filters.sortDirection
            }),
            ...(filters.startDate && { startDate: filters.startDate.getTime() }),
            ...(filters.endDate && {
                endDate: new Date(filters.endDate).setHours(23, 59, 59, 999)
            })
        };

        // Use debounced version for search, immediate for other filters
        if (filters.applicationId) {
            debouncedFetchData(params);
        } else {
            debouncedFetchData.cancel();
            await fetchData(params);
        }
    };

    const updateFilters = (newFilters) => {
        console.log("New filters: ", { ...newFilters })
        setFilters(prev => ({ ...prev, ...newFilters }));
    };

    // Fetch data when filters change
    useEffect(() => {
        fetchApplications();
    }, [filters]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            debouncedFetchData.cancel();
        };
    }, []);

    return {
        data,
        isLoading,
        error,
        pagination,
        filters,
        updateFilters,
        fetchApplications
    };
};
